// @ts-check
/**
 * Rules for "is this dashboard env safe to bake into a bundle?"
 *
 * Plain ESM JavaScript on purpose: the same module runs in three places with no
 * build step between them —
 *   - vite.config.ts        → fails `vite build` before anything is emitted
 *   - scripts/check-env.mjs → CLI pre-check the deploy scripts run on the box
 *   - src/shared/config.ts  → runtime guard inside the served bundle
 *
 * Types live next door in envCheck.d.mts.
 */

export const REQUIRED_VARS = Object.freeze(['VITE_API_URL', 'VITE_SUPABASE_URL', 'VITE_SUPABASE_ANON_KEY']);

const LOCAL_HOSTNAMES = new Set(['localhost', '127.0.0.1', '0.0.0.0', '[::1]', '::1']);

/** @param {string} value */
function parseHttpUrl(value) {
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:' ? url : null;
  } catch {
    return null;
  }
}

/**
 * True when a URL points at the machine that serves the bundle rather than a
 * public origin — the exact mistake that put `apiUrl:"http://localhost:3000"`
 * into the live app.lienguardian.ai bundle.
 * @param {string} value
 */
export function isLocalUrl(value) {
  const url = parseHttpUrl(value);
  if (url) return LOCAL_HOSTNAMES.has(url.hostname) || url.hostname.endsWith('.localhost');
  return /(^|\/\/)(localhost|127\.0\.0\.1|0\.0\.0\.0)([:/]|$)/i.test(value);
}

/**
 * @param {import('./envCheck.mjs').DashboardEnvInput} env
 * @param {{ allowLocal: boolean }} opts `allowLocal` is true only for development-style builds.
 * @returns {string[]} human-readable problems; an empty array means the env is safe to ship.
 */
export function dashboardEnvProblems(env, { allowLocal }) {
  /** @type {string[]} */
  const problems = [];

  const apiUrl = (env.VITE_API_URL ?? '').trim();
  if (!apiUrl) {
    problems.push('VITE_API_URL is missing — every API request in the bundle would go to the wrong origin');
  } else if (!parseHttpUrl(apiUrl)) {
    problems.push(`VITE_API_URL is not an http(s) URL: "${apiUrl}"`);
  } else {
    if (!allowLocal && isLocalUrl(apiUrl)) {
      problems.push(`VITE_API_URL points at a local address in a production-style build: "${apiUrl}"`);
    }
    if (apiUrl.endsWith('/')) {
      problems.push(`VITE_API_URL must not end with a slash (requests are built as VITE_API_URL + "/api/..."): "${apiUrl}"`);
    }
  }

  const supabaseUrl = (env.VITE_SUPABASE_URL ?? '').trim();
  if (!supabaseUrl) {
    problems.push('VITE_SUPABASE_URL is missing — sign-in would be disabled in the served bundle');
  } else if (!parseHttpUrl(supabaseUrl)) {
    problems.push(`VITE_SUPABASE_URL is not an http(s) URL: "${supabaseUrl}"`);
  } else if (!allowLocal && isLocalUrl(supabaseUrl)) {
    problems.push(`VITE_SUPABASE_URL points at a local address in a production-style build: "${supabaseUrl}"`);
  }

  const anonKey = (env.VITE_SUPABASE_ANON_KEY ?? '').trim();
  if (!anonKey) {
    problems.push('VITE_SUPABASE_ANON_KEY is missing — sign-in would be disabled in the served bundle');
  } else if (anonKey.length < 20) {
    problems.push('VITE_SUPABASE_ANON_KEY looks truncated (shorter than 20 characters)');
  }

  if (!allowLocal && (env.VITE_DEV_TOKEN ?? '').trim()) {
    problems.push('VITE_DEV_TOKEN is set — it is a local-only static token and must never be baked into a deployed bundle');
  }

  return problems;
}

/**
 * @param {readonly string[]} problems
 * @param {{ mode: string; source: string }} context
 */
export function formatEnvReport(problems, { mode, source }) {
  const lines = [
    `Dashboard env is not safe for a "${mode}" build (read from ${source}):`,
    ...problems.map((p) => `  - ${p}`),
    '',
    `Required: ${REQUIRED_VARS.join(', ')}. See dashboard/.env.example and ops/dashboard.env.empire.`,
    'For a local test build against a local API use `vite build --mode development`.',
  ];
  return lines.join('\n');
}
